import { useState, useEffect } from "react";
import { ScrollArea } from "./ui/scroll-area";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Button } from "./ui/button";
import { cn } from "../lib/utils";
import { Clock, Globe, Download, Users, Search, Heart, Star, RotateCcw } from "lucide-react";
import KofiModal from "./KofiModal";
import SiteStatsWidget from "./SiteStatsWidget";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_spawn-monitor/artifacts/tftx3qti_Gemini_Generated_Image_dov1ndov1ndov1nd.png";

const BossSidebar = ({
  bosses,
  selectedBossIndex,
  onSelectBoss,
  getBossImage,
  now,
  userName,
  onUserNameChange,
  user,
  trackerType,
  syncToGlobal,
  onSyncToGlobalChange,
  onSyncFromGlobal,
  lastSyncId,
  canRevertUntil,
  onRevertSync,
  guilds = [],
  syncToGuild,
  onSyncToGuildChange,
  selectedSyncGuildId,
  onSelectedSyncGuildChange,
  favorites = [],
  onToggleFavorite,
  showFavoritesOnly,
  onShowFavoritesOnlyChange,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [ukTime, setUkTime] = useState("");
  const [showKofiModal, setShowKofiModal] = useState(false);

  // Update UTC time every second
  useEffect(() => {
    const updateUkTime = () => {
      const now = new Date();
      const ukTimeStr = now.toLocaleString("en-GB", {
        timeZone: "UTC",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });
      setUkTime(ukTimeStr);
    };
    
    updateUkTime();
    const interval = setInterval(updateUkTime, 1000);
    return () => clearInterval(interval);
  }, []);

  // Filter bosses based on search query and favorites
  const filteredBosses = bosses.filter((boss) => {
    const matchesSearch = !searchQuery.trim() || 
      boss.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFavorites = !showFavoritesOnly || favorites.includes(boss.name);
    return matchesSearch && matchesFavorites;
  });
  // Calculate boss summary (UP and WINDOW counts)
  const getBossSummary = (boss) => {
    let up = 0;
    let window = 0;

    if (!boss.locations || !Array.isArray(boss.locations)) return { up, window };
    if (typeof boss.baseTime !== 'number' || isNaN(boss.baseTime)) return { up, window };

    boss.locations.forEach((loc) => {
      if (!loc.channels || !Array.isArray(loc.channels)) return;
      loc.channels.forEach((channel) => {
        if (!channel || channel.time === 0) return;

        const baseMs = boss.baseTime * 60 * 1000;
        const minSpawnMs = channel.time + baseMs * 0.8;
        const maxSpawnMs = channel.time + baseMs * 1.2;
        const twoHoursMs = 2 * 60 * 60 * 1000;

        if (now > maxSpawnMs + twoHoursMs) {
          // EXPIRED, do not count as UP
        } else if (now >= maxSpawnMs) {
          up++;
        } else if (now >= minSpawnMs && now < maxSpawnMs) {
          window++;
        }
      });
    });

    return { up, window };
  };

  return (
    <div className="h-full flex flex-col bg-card" data-testid="boss-sidebar">
      {/* Header with Large Logo */}
      <div className="p-4 border-b border-border">
        <div className="flex flex-col items-center gap-3">
          <div className="w-28 h-28 rounded-full overflow-hidden flex-shrink-0 bg-background border-2 border-primary/30">
            <img 
              src={LOGO_URL} 
              alt="OreStore Boss Area Tracker" 
              className="w-full h-full object-cover"
            />
          </div>
          <div className="text-center">
            <h1 className="font-bold text-lg leading-tight">OreStore Boss Area Tracker</h1>
            <p className="text-xs text-muted-foreground capitalize">{trackerType} Timers</p>
          </div>
          {/* Server Time (UTC) */}
          <div className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 rounded-lg">
            <Clock className="w-4 h-4 text-primary" />
            <span className="text-sm font-mono font-medium">{ukTime}</span>
            <span className="text-xs text-muted-foreground">UTC</span>
          </div>
        </div>
      </div>

      {/* User Name Input */}
      <div className="px-4 py-3 border-b border-border bg-background/50 space-y-3">
        <div>
          <Label htmlFor="username" className="text-xs text-muted-foreground uppercase tracking-wider font-bold mb-2 block">
            {user ? "Logged in as" : "Your Name (Killer)"}
          </Label>
          {user ? (
            <div className="flex items-center gap-2 px-3 py-2 bg-primary/10 rounded-lg">
              {user.avatar ? (
                <img 
                  src={user.avatar} 
                  alt={user.username} 
                  className="w-6 h-6 rounded-full object-cover"
                />
              ) : (
                <div className="w-6 h-6 bg-primary/20 rounded-full flex items-center justify-center">
                  <span className="text-xs font-bold text-primary">
                    {user.username.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              <span className="text-sm font-medium">{user.username}</span>
            </div>
          ) : (
            <Input
              id="username"
              placeholder="Anonymous"
              value={userName}
              onChange={onUserNameChange}
              className="h-9"
              data-testid="username-input"
            />
          )}
        </div>

        {/* Sync options (only for personal/guild trackers) */}
        {trackerType !== "global" && (
          <>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-muted-foreground" />
                <Label htmlFor="syncToGlobal" className="text-sm cursor-pointer">
                  Sync to Global
                </Label>
              </div>
              <Switch
                id="syncToGlobal"
                checked={syncToGlobal}
                onCheckedChange={onSyncToGlobalChange}
                data-testid="sync-to-global-switch"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              className="w-full gap-2"
              onClick={onSyncFromGlobal}
              data-testid="sync-from-global-button"
            >
              <Download className="w-4 h-4" />
              Sync from Global
            </Button>
            
            {/* Revert Sync Button - shows for 3 minutes after sync */}
            {lastSyncId && canRevertUntil && new Date() < canRevertUntil && (
              <Button
                variant="destructive"
                size="sm"
                className="w-full gap-2 animate-pulse"
                onClick={onRevertSync}
                data-testid="revert-sync-button"
              >
                <RotateCcw className="w-4 h-4" />
                Revert Sync
              </Button>
            )}
          </>
        )}

        {/* Personal ↔ Guild Sync (only for personal tracker when user has guilds) */}
        {trackerType === "personal" && guilds.length > 0 && (
          <div className="pt-3 border-t border-border space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-muted-foreground" />
                <Label htmlFor="syncToGuild" className="text-sm cursor-pointer">
                  Sync to Guild
                </Label>
              </div>
              <Switch
                id="syncToGuild"
                checked={syncToGuild}
                onCheckedChange={onSyncToGuildChange}
                data-testid="sync-to-guild-switch"
              />
            </div>
            {syncToGuild && guilds.length > 1 && (
              <select
                value={selectedSyncGuildId || ""}
                onChange={(e) => onSelectedSyncGuildChange(e.target.value)}
                className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm"
                data-testid="sync-guild-selector"
              >
                <option value="ALL">All My Guilds</option>
                {guilds.map((guild) => (
                  <option key={guild.id} value={guild.id}>
                    {guild.name}
                  </option>
                ))}
              </select>
            )}
            {syncToGuild && (
              <p className="text-xs text-muted-foreground">
                Kills marked in Personal will also update {guilds.length === 1 ? guilds[0].name : "selected guild"}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Boss Search Filter */}
      <div className="px-4 py-2 border-b border-border space-y-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search boss..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 h-9"
            data-testid="boss-search-input"
          />
        </div>
        {/* Favorites Filter Toggle */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => onShowFavoritesOnlyChange(!showFavoritesOnly)}
            className={cn(
              "flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors",
              showFavoritesOnly
                ? "bg-amber-500/20 text-amber-400 border border-amber-500/50"
                : "bg-secondary/50 text-muted-foreground hover:bg-secondary"
            )}
            data-testid="favorites-filter-toggle"
          >
            <Star className={cn("w-3.5 h-3.5", showFavoritesOnly && "fill-amber-400")} />
            {showFavoritesOnly ? "Showing Favorites" : "Show Favorites Only"}
          </button>
          {favorites.length > 0 && (
            <span className="text-xs text-muted-foreground">
              {favorites.length} favorite{favorites.length !== 1 ? "s" : ""}
            </span>
          )}
        </div>
      </div>

      {/* Boss List - Takes remaining space and scrolls */}
      <ScrollArea className="flex-1 min-h-0">
        <div className="p-2" data-testid="boss-list">
          {filteredBosses.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p className="text-sm">No bosses found</p>
            </div>
          ) : (
            filteredBosses.map((boss, index) => {
              // Find the original index for selection
              const originalIndex = bosses.findIndex((b) => b.name === boss.name);
              const summary = getBossSummary(boss);
              const isSelected = selectedBossIndex === originalIndex;
              const isFavorite = favorites.includes(boss.name);

            return (
              <div
                key={boss.name}
                className={cn(
                  "w-full flex items-center gap-3 p-3 rounded-lg transition-all duration-200 text-left mb-1 relative group",
                  isSelected
                    ? "bg-primary/20 border border-primary/50"
                    : "hover:bg-secondary border border-transparent"
                )}
                data-testid={`boss-item-${originalIndex}`}
              >
                {/* Favorite Star Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleFavorite(boss.name);
                  }}
                  className={cn(
                    "absolute top-2 right-2 p-1 rounded transition-all z-10",
                    isFavorite 
                      ? "text-amber-400 opacity-100" 
                      : "text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-amber-400"
                  )}
                  title={isFavorite ? "Remove from favorites" : "Add to favorites"}
                  data-testid={`favorite-toggle-${boss.name}`}
                >
                  <Star className={cn("w-4 h-4", isFavorite && "fill-amber-400")} />
                </button>

                {/* Clickable area for selecting boss */}
                <button
                  onClick={() => onSelectBoss(originalIndex)}
                  className="flex items-center gap-3 flex-1 min-w-0"
                >
                  {/* Boss Image */}
                  <div className="w-12 h-12 bg-background rounded-lg flex-shrink-0 flex items-center justify-center overflow-hidden">
                    <img
                      src={getBossImage(boss.imageKey)}
                      alt={boss.name}
                      className="max-w-full max-h-full object-contain"
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  </div>

                  {/* Boss Info */}
                  <div className="flex-1 min-w-0 text-left">
                    <p className={cn(
                      "font-semibold text-sm truncate",
                      isSelected && "text-primary"
                    )}>
                      {boss.name}
                    </p>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                      <Clock className="w-3 h-3" />
                      <span>{boss.baseTime / 60}h</span>
                    </div>
                  </div>

                  {/* Status Badges */}
                  <div className="flex flex-col gap-1 items-end">
                    {summary.up > 0 && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-emerald-500/20 text-emerald-400 border border-emerald-500/50">
                        {summary.up} UP
                      </span>
                    )}
                    {summary.window > 0 && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-amber-500/20 text-amber-400 border border-amber-500/50 animate-pulse-window">
                        {summary.window} W
                      </span>
                    )}
                  </div>
                </button>
              </div>
            );
          })
          )}
        </div>

        {/* Site Stats Widget - Inside ScrollArea */}
        <div className="px-4 py-3 border-t border-border">
          <SiteStatsWidget />
        </div>

        {/* OreStore Advertisement Banner - Inside ScrollArea */}
        <div className="p-4 border-t border-border bg-gradient-to-br from-amber-900/20 to-amber-800/10">
          <div className="flex justify-center mb-3">
            <img 
              src="https://quavir.pl/CMS/Ore-Store-Logo.png" 
              alt="Ore Store Logo" 
              className="max-h-14 object-contain rounded drop-shadow-md"
            />
          </div>
          <p className="text-xs text-center text-amber-100/80 leading-relaxed">
            We are always at the same place at{" "}
            <span className="font-bold text-amber-400">Free Market 15</span> on{" "}
            <span className="font-bold text-amber-400">Channel 15</span>. 
            Our stock is replenished everyday - all kinds of ores, raw and refined, and crystals!
          </p>
        </div>

        {/* Ko-fi Support Link - Inside ScrollArea */}
        <div className="px-4 py-3 border-t border-border bg-background/30">
          <button 
            onClick={() => setShowKofiModal(true)}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-[#FF5E5B]/10 hover:bg-[#FF5E5B]/20 border border-[#FF5E5B]/30 rounded-lg transition-colors"
            data-testid="kofi-link"
          >
            <Heart className="w-5 h-5 text-[#FF5E5B] fill-[#FF5E5B]" />
            <span className="text-sm font-medium text-[#FF5E5B]">Support on Ko-fi</span>
          </button>
          <p className="text-[10px] text-center text-muted-foreground mt-2 leading-relaxed">
            Help keep hosting running. No additional benefits for supporters - just our gratitude!
          </p>
        </div>

        {/* Footer - Inside ScrollArea */}
        <div className="px-4 py-2 border-t border-border bg-background/50">
          <p className="text-xs text-muted-foreground text-center">
            Auto-refresh every 5 seconds
          </p>
        </div>
      </ScrollArea>

      {/* Ko-fi Modal */}
      <KofiModal open={showKofiModal} onOpenChange={setShowKofiModal} />
    </div>
  );
};

export default BossSidebar;
